import { type AdminModel, type AdminModels, CSRF_HEADER } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ChevronDown,
  CircleCheck,
  CircleDashed,
  Gauge,
  Globe,
  Lock,
  ServerCog,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ApiError, ok } from "../api-errors.ts";
import { formatDate } from "../i18n/format.ts";
import {
  implicationLines,
  roleList,
  uncoveredBy,
  uncoveredRefusal,
} from "../models/model-labels.ts";
import { navigate, useSearch } from "../router.tsx";
import { useSession } from "../session.tsx";
import { SettingsGroupTabs } from "../settings-shell/SettingsGroupTabs.tsx";
import { SettingsGroupPage, SettingsShell } from "../settings-shell/SettingsShell.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { treeRows, useAdminEntities } from "./entity-tree.ts";
import {
  AllowedModelsLine,
  EffortFamiliesSection,
  HealthSection,
  LabsSection,
  PinsSection,
  useModelsData,
} from "./ModelsSections.tsx";

const EFFORTS = ["fast", "chat", "reasoning"] as const;
const modelsKey = (level: string | null) => ["admin", "models", level ?? "tenant"] as const;

/** The screen's sections, each its own page (`/backoffice/models?section=<tab>`). */
export const MODELS_TABS = ["labs", "efforts", "pins", "health", "access"] as const;
export type ModelsTab = (typeof MODELS_TABS)[number];

/** A section's URL at a level: the bare URL is the first section. */
function tabPath(base: string, tab: ModelsTab, level: string | null): string {
  const params = new URLSearchParams();
  if (tab !== MODELS_TABS[0]) {
    params.set("section", tab);
  }
  if (level) {
    params.set("level", level);
  }
  const query = params.toString();
  return `${base}/models${query ? `?${query}` : ""}`;
}

/**
 * The backoffice Models screen (spec `tenant-administration` "Model policy administration",
 * openspec change `model-picking` D10), on the shared settings shell: one page per section
 * (Labs, Efforts, Pins, Health and history, Models and consent) at its own URL, the
 * level (the organisation or an entity) in `?level=`. Entity levels show the same rows with
 * their inherited values and locks.
 */
export function ModelsScreen() {
  const { admin, readOnly, base } = useAdminApi();
  const { t } = useTranslation();
  const id = useId();
  const search = useSearch();
  const entities = useAdminEntities();
  const rows = entities.data ? treeRows(entities.data) : [];
  const tenantAdmin = useSession().roles.includes("tenant_admin");
  const picked = search.get("level");
  // The Reasoning section moved into Efforts (Q19): an older link lands there.
  const section = search.get("section");
  const asked = section === "reasoning" ? "efforts" : section;
  const tab: ModelsTab = (MODELS_TABS as readonly string[]).includes(asked ?? "")
    ? (asked as ModelsTab)
    : MODELS_TABS[0];
  const level =
    picked && rows.some((r) => r.entity.id === picked)
      ? picked
      : tenantAdmin
        ? null
        : (rows[0]?.entity.id ?? null);
  const models = useQuery({
    queryKey: modelsKey(level),
    enabled: entities.isSuccess || entities.isError,
    queryFn: async (): Promise<AdminModels> =>
      ok(await admin.models.$get({ query: level ? { entityId: level } : {} })),
    retry: false,
  });
  const heading = useRef<HTMLHeadingElement>(null);
  const shown = useRef(tab);
  // A section change moves focus to its heading (the shell's rule), never on first load.
  useEffect(() => {
    if (shown.current !== tab) {
      shown.current = tab;
      heading.current?.focus();
    }
  }, [tab]);

  if (models.error instanceof ApiError && models.error.status === 403) {
    return <Notice>{t("adminSettings.adminsOnly")}</Notice>;
  }
  return (
    <div className="flex flex-col gap-6">
      <SettingsShell
        title={t("models.title")}
        lead={t("models.intro")}
        controls={
          rows.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-level`}>{t("models.level")}</Label>
              <NativeSelect
                id={`${id}-level`}
                value={level ?? ""}
                onChange={(e) => navigate(tabPath(base, tab, e.currentTarget.value || null))}
              >
                {tenantAdmin ? <option value="">{t("models.organisation")}</option> : null}
                {rows.map((r) => (
                  <option key={r.entity.id} value={r.entity.id}>
                    {`${" ".repeat(r.depth)}${r.entity.name}`}
                  </option>
                ))}
              </NativeSelect>
            </div>
          ) : null
        }
        tabs={
          <SettingsGroupTabs
            label={t("models.sections.tabs.label")}
            groups={MODELS_TABS}
            current={tab}
            href={(g) => tabPath(base, g, level)}
            groupLabel={(g) => t(`models.sections.tabs.${g}`)}
            count={() => 0}
            countLabel={() => ""}
          />
        }
      >
        {models.isError ? (
          <Notice tone="danger">{models.error.message}</Notice>
        ) : !models.data ? (
          <Skeleton className="h-96 w-full" />
        ) : (
          <SettingsGroupPage
            id={`models-${tab}`}
            heading={t(`models.sections.tabs.${tab}`)}
            headingRef={heading}
            purpose={t(`models.sections.purpose.${tab}`)}
          >
            <ModelsTabBody
              key={`${models.data.entityId ?? "tenant"}-${tab}`}
              tab={tab}
              data={models.data}
              // Writes go to the level the server resolved (an entity admin's fallback entity).
              level={models.data.entityId}
              readOnly={readOnly}
            />
          </SettingsGroupPage>
        )}
      </SettingsShell>
    </div>
  );
}

function ModelsTabBody(props: {
  tab: ModelsTab;
  data: AdminModels;
  level: string | null;
  readOnly: boolean;
}) {
  const { tab, data, level, readOnly } = props;
  const sections = useModelsData(level);
  switch (tab) {
    case "labs":
      return <LabsSection level={level} data={sections} readOnly={readOnly} />;
    case "efforts":
      return <EffortsBody data={data} level={level} readOnly={readOnly} sections={sections} />;
    case "pins":
      return <PinsSection level={level} data={sections} models={data} readOnly={readOnly} />;
    case "health":
      return <HealthSection level={level} data={sections} readOnly={readOnly} />;
    case "access":
      return <AccessBody data={data} level={level} readOnly={readOnly} />;
  }
}

/** Efforts: which efforts people may use, family choice, then the family per effort. */
function EffortsBody(props: {
  data: AdminModels;
  level: string | null;
  readOnly: boolean;
  sections: ReturnType<typeof useModelsData>;
}) {
  const { data, level } = props;
  const { t } = useTranslation();
  const uncovered = data.roles.filter((r) => r.needed && r.usable.length === 0).map((r) => r.role);
  return (
    <>
      {uncovered.length > 0 ? (
        <Notice tone="warning">{t("models.uncovered", { roles: roleList(uncovered) })}</Notice>
      ) : null}
      <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
        <EffortsRow data={data} level={level} readOnly={props.readOnly} />
        <PickerRow data={data} level={level} readOnly={props.readOnly} />
      </ul>
      <section aria-labelledby="models-effort-families" className="flex flex-col gap-3">
        <h3 id="models-effort-families" className="text-sm font-semibold text-fg">
          {t("models.sections.efforts.familyHeading")}
        </h3>
        <EffortFamiliesSection level={level} data={props.sections} readOnly={props.readOnly} />
      </section>
    </>
  );
}

/** Models and consent: the instance line, the catalogue with consent, the allowed models. */
function AccessBody(props: { data: AdminModels; level: string | null; readOnly: boolean }) {
  const { data, level } = props;
  const { t } = useTranslation();
  const manage = data.canManageConsent && !props.readOnly;
  const uncovered = data.roles.filter((r) => r.needed && r.usable.length === 0).map((r) => r.role);
  return (
    <>
      {uncovered.length > 0 ? (
        <Notice tone="warning">{t("models.uncovered", { roles: roleList(uncovered) })}</Notice>
      ) : null}
      <p className="flex items-center gap-1.5 text-xs text-fg-muted">
        <ServerCog aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
        {t("models.instance", {
          geographies: data.geographies.map((g) => t(`models.geographies.${g}`)).join(" + "),
          mode: t(`models.retentionMode.${data.retentionMode}`),
        })}
      </p>
      <AllowedModelsLine models={data} />
      <AdvancedModels data={data} level={level} readOnly={props.readOnly} />
      <section aria-labelledby="models-catalogue" className="flex flex-col gap-3">
        <h3 id="models-catalogue" className="text-sm font-semibold text-fg">
          {t("models.catalogue")}
        </h3>
        {data.canManageConsent ? null : (
          <p className="text-sm text-fg-secondary">{t("models.readOnly")}</p>
        )}
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
          {data.models.map((model) => (
            <CatalogueRow key={model.key} model={model} manage={manage} />
          ))}
        </ul>
      </section>
    </>
  );
}

/** One catalogue model: name, offer and consent line, implications, and the consent actions. */
function CatalogueRow({ model, manage }: { model: AdminModel; manage: boolean }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<"allow" | "revoke" | null>(null);
  const live = model.consent.state === "accepted";
  return (
    <li className="flex flex-col gap-3 px-4 py-3.5 @container">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-medium text-fg">{model.displayName}</span>
          <StatusLine model={model} />
        </div>
        {/* A live consent can always be revoked, even once the instance stops offering it. */}
        {manage && model.consentRequired && (live || model.offered) ? (
          live ? (
            <Button size="sm" variant="ghost" onClick={() => setDialog("revoke")}>
              {t("models.revoke")}
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setDialog("allow")}>
              {t("models.allow")}
            </Button>
          )
        ) : null}
      </div>
      <Implications model={model} />
      <ConsentDialog model={model} open={dialog === "allow"} onClose={() => setDialog(null)} />
      <RevokeDialog model={model} open={dialog === "revoke"} onClose={() => setDialog(null)} />
    </li>
  );
}

function StatusLine({ model }: { model: AdminModel }) {
  const { t } = useTranslation();
  const c = model.consent;
  const date = (iso: string | null) => (iso ? formatDate(iso) : "");
  const consent =
    c.state === "accepted"
      ? c.acceptedByName
        ? t("models.consent.accepted", { name: c.acceptedByName, date: date(c.acceptedAt) })
        : t("models.consent.acceptedNoName", { date: date(c.acceptedAt) })
      : c.state === "revoked"
        ? t("models.consent.revoked", { date: date(c.revokedAt) })
        : t(`models.consent.${c.state}`);
  const tone =
    c.state === "accepted"
      ? "text-success-fg"
      : c.state === "outdated" || c.state === "none"
        ? "text-warning-fg"
        : "text-fg-muted";
  const ConsentIcon =
    c.state === "accepted"
      ? CircleCheck
      : c.state === "not_required"
        ? CircleDashed
        : TriangleAlert;
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted">
      {model.offered ? null : (
        <span className="inline-flex items-center gap-1">
          <Lock aria-hidden="true" className="size-3 shrink-0" />
          {t(`models.unavailable.${model.unavailableReason ?? "retention"}`)}
        </span>
      )}
      {model.consentRequired || c.state !== "not_required" ? (
        <>
          {model.offered ? null : <span aria-hidden="true">·</span>}
          <span className={cn("inline-flex items-center gap-1", tone)}>
            <ConsentIcon aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
            {consent}
          </span>
        </>
      ) : null}
    </p>
  );
}

const IMPLICATION_ICONS = [Archive, Globe, ShieldCheck, Gauge];

/** The implications as quiet icon-and-text lines (the provider line only when it holds). */
function Implications({ model }: { model: Pick<AdminModel, "implications"> }) {
  const lines = implicationLines(model);
  const icons = model.implications.providerReceivesData
    ? [Archive, Globe, Gauge]
    : IMPLICATION_ICONS;
  return (
    <ul className="grid gap-x-6 gap-y-1 text-[13px] text-fg-secondary @2xl:grid-cols-2">
      {lines.map((line, i) => {
        const Icon = icons[i] ?? Archive;
        return (
          <li key={line} className="flex items-start gap-2">
            <Icon
              aria-hidden="true"
              className="mt-0.5 size-3.5 shrink-0 text-fg-muted"
              strokeWidth={1.75}
            />
            <span>{line}</span>
          </li>
        );
      })}
    </ul>
  );
}

function useConsentMutation(model: AdminModel, method: "put" | "delete", onDone: () => void) {
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = { headers: { [CSRF_HEADER]: csrfToken } };
      const route = admin.models[":model"].consent;
      return method === "put"
        ? ok(
            await route.$put(
              {
                param: { model: model.key },
                json: { implicationsVersion: model.implicationsVersion },
              },
              headers,
            ),
          )
        : ok(await route.$delete({ param: { model: model.key } }, headers));
    },
    onSuccess: onDone,
    // Every level's view shows consent and coverage.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["admin", "models"] }),
  });
}

/** Accepting states every implication and needs an explicit confirmation. */
function ConsentDialog(props: { model: AdminModel; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const [confirmed, setConfirmed] = useState(false);
  // Each opening, and new terms (after a 409), need their own confirmation.
  const version = props.model.implicationsVersion;
  // biome-ignore lint/correctness/useExhaustiveDependencies: resets on these changes only
  useEffect(() => setConfirmed(false), [version, props.open]);
  const accept = useConsentMutation(props.model, "put", props.onClose);
  const changed = accept.error instanceof ApiError && accept.error.code === "implications_changed";
  const name = props.model.displayName;
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("models.dialog.title", { model: name })}
      description={t("models.dialog.lead")}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (confirmed && !accept.isPending) {
            accept.mutate();
          }
        }}
      >
        <div className="rounded-lg border border-line bg-surface-raised px-4 py-3">
          <Implications model={props.model} />
        </div>
        <p className="text-sm text-fg-secondary">{t("models.dialog.scope")}</p>
        <div className="flex items-start gap-2.5">
          <Checkbox
            id={`${id}-confirm`}
            checked={confirmed}
            onCheckedChange={(on) => setConfirmed(on === true)}
            className="mt-0.5"
          />
          <Label htmlFor={`${id}-confirm`} className="font-normal leading-snug">
            {t("models.dialog.confirm")}
          </Label>
        </div>
        {accept.isError ? (
          <Notice tone="danger">
            {changed ? t("models.dialog.changed") : accept.error.message}
          </Notice>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("settings.cancel")}
          </Button>
          <Button type="submit" disabled={!confirmed || accept.isPending}>
            {t("models.dialog.submit", { model: name })}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}

function RevokeDialog(props: { model: AdminModel; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const revoke = useConsentMutation(props.model, "delete", props.onClose);
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("models.revokeDialog.title", { model: props.model.displayName })}
      description={t("models.revokeDialog.lead")}
    >
      {revoke.isError ? <Notice tone="danger">{revoke.error.message}</Notice> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={props.onClose}>
          {t("settings.cancel")}
        </Button>
        <Button variant="destructive" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
          {t("models.revokeDialog.submit")}
        </Button>
      </div>
    </AppDialog>
  );
}

/** Writes one policy key at the level (tenant or entity), with its lock. */
function useSettingWrite(level: string | null, key: string) {
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (json: { value: unknown; locked: boolean }) => {
      const headers = { headers: { [CSRF_HEADER]: csrfToken } };
      return level === null
        ? ok(await admin.settings[":key"].$put({ param: { key }, json }, headers))
        : ok(
            await admin.entities[":entityId"].settings[":key"].$put(
              { param: { entityId: level, key }, json },
              headers,
            ),
          );
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "models"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "settings"] }),
      ]),
  });
}

function SourceLine(props: { source: string; locked: boolean }) {
  const { t } = useTranslation();
  return (
    <p className="flex flex-wrap items-center gap-x-2 text-xs text-fg-muted">
      <span>{t(`models.source.${props.source as "tenant"}`)}</span>
      {props.locked ? (
        <span className="inline-flex items-center gap-1">
          <Lock aria-hidden="true" className="size-3" />
          {t("models.lockedAbove")}
        </span>
      ) : null}
    </p>
  );
}

/** The row frame shared by the policy settings: label and help, editor, source and save. */
function PolicyRow(props: {
  label: string;
  help: string;
  source: string;
  locked: boolean;
  editable: boolean;
  children: ReactNode;
  lock: { checked: boolean; onChange: (on: boolean) => void };
  save: { disabled: boolean; pending: boolean; onSave: () => void; error: string | null };
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <li className="flex flex-col gap-3 px-4 py-3.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span id={`${id}-label`} className="text-sm font-medium text-fg">
          {props.label}
        </span>
        <span className="text-sm text-fg-secondary">{props.help}</span>
      </div>
      <fieldset aria-labelledby={`${id}-label`} disabled={!props.editable}>
        {props.children}
      </fieldset>
      <SourceLine source={props.source} locked={props.locked} />
      {props.editable ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${id}-lock`}
              checked={props.lock.checked}
              onCheckedChange={(on) => props.lock.onChange(on === true)}
            />
            <Label htmlFor={`${id}-lock`} className="font-normal text-fg-secondary">
              {t("models.lock")}
            </Label>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={props.save.disabled}
            onClick={props.save.onSave}
          >
            {props.save.pending ? t("models.saving") : t("models.save")}
          </Button>
          {props.save.error ? <InlineError>{props.save.error}</InlineError> : null}
        </div>
      ) : null}
    </li>
  );
}

function EffortsRow(props: { data: AdminModels; level: string | null; readOnly: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  const setting = props.data.policy.allowed;
  const [value, setValue] = useState<string[]>(setting.value);
  const [locked, setLocked] = useState(setting.lockedHere);
  const write = useSettingWrite(props.level, "models.allowed");
  const changed = JSON.stringify([...value].sort()) !== JSON.stringify([...setting.value].sort());
  return (
    <PolicyRow
      label={t("models.efforts")}
      help={t("models.effortsHelp")}
      source={setting.source}
      locked={setting.locked}
      editable={!props.readOnly && !setting.locked}
      lock={{ checked: locked, onChange: setLocked }}
      save={{
        disabled: (!changed && locked === setting.lockedHere) || write.isPending,
        pending: write.isPending,
        onSave: () => write.mutate({ value, locked }),
        error: write.error?.message ?? null,
      }}
    >
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {EFFORTS.map((effort) => {
          const on = value.includes(effort);
          // Excluded above: this level cannot allow it.
          const excluded = !setting.inherited.includes(effort);
          return (
            <div key={effort} className="flex items-center gap-2">
              <Checkbox
                id={`${id}-${effort}`}
                checked={on}
                // At least one effort stays.
                disabled={(on && value.length === 1) || (excluded && !on)}
                onCheckedChange={(next) =>
                  setValue(
                    next === true
                      ? EFFORTS.filter((e) => e === effort || value.includes(e))
                      : value.filter((e) => e !== effort),
                  )
                }
              />
              <Label htmlFor={`${id}-${effort}`} className="font-normal">
                {t(`adminSettings.modelRoles.${effort}`)}
              </Label>
            </div>
          );
        })}
      </div>
    </PolicyRow>
  );
}

function PickerRow(props: { data: AdminModels; level: string | null; readOnly: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  const setting = props.data.policy.advancedPicker;
  const [value, setValue] = useState(setting.value);
  const [locked, setLocked] = useState(setting.lockedHere);
  const write = useSettingWrite(props.level, "models.advancedPicker");
  return (
    <PolicyRow
      label={t("models.advancedPicker")}
      help={t("models.advancedPickerHelp")}
      source={setting.source}
      locked={setting.locked}
      editable={!props.readOnly && !setting.locked}
      lock={{ checked: locked, onChange: setLocked }}
      save={{
        disabled: (value === setting.value && locked === setting.lockedHere) || write.isPending,
        pending: write.isPending,
        onSave: () => write.mutate({ value, locked }),
        error: write.error?.message ?? null,
      }}
    >
      <div className="flex items-center gap-2.5">
        <Checkbox
          id={`${id}-on`}
          checked={value}
          // Off above: this level cannot turn it on.
          disabled={!setting.inherited && !value}
          onCheckedChange={(on) => setValue(on === true)}
        />
        <Label htmlFor={`${id}-on`} className="font-normal">
          {value ? t("models.on") : t("models.off")}
        </Label>
      </div>
    </PolicyRow>
  );
}

/**
 * The allowed models, behind a disclosure: every catalogue model, those no role can use at this
 * level disabled. Saving is blocked while a needed role would have no usable model (the server
 * also refuses it, for the levels below).
 */
function AdvancedModels(props: { data: AdminModels; level: string | null; readOnly: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const setting = props.data.policy.allowedModels;
  const [value, setValue] = useState<string[]>(setting.value);
  const [locked, setLocked] = useState(setting.lockedHere);
  const write = useSettingWrite(props.level, "models.allowedModels");
  const usableAnywhere = new Set(props.data.roles.flatMap((r) => r.candidates));
  const uncovered = uncoveredBy(value, props.data.roles);
  const changed = JSON.stringify([...value].sort()) !== JSON.stringify([...setting.value].sort());
  return (
    <div className="flex flex-col gap-3">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        aria-expanded={open}
        aria-controls={`${id}-advanced`}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "size-4 transition-transform duration-(--duration-fast)",
            open ? "rotate-180" : "",
          )}
        />
        {t("models.advanced")}
      </Button>
      {open ? (
        <ul
          id={`${id}-advanced`}
          className="divide-y divide-line rounded-lg border border-line bg-surface-raised"
        >
          <PolicyRow
            label={t("models.advanced")}
            help={t("models.advancedHelp")}
            source={setting.source}
            locked={setting.locked}
            editable={!props.readOnly && !setting.locked}
            lock={{ checked: locked, onChange: setLocked }}
            save={{
              disabled:
                (!changed && locked === setting.lockedHere) ||
                uncovered.length > 0 ||
                value.length === 0 ||
                write.isPending,
              pending: write.isPending,
              onSave: () => write.mutate({ value, locked }),
              error: write.error ? (uncoveredRefusal(write.error) ?? write.error.message) : null,
            }}
          >
            <div className="flex flex-col gap-2">
              {props.data.models.map((model) => {
                // The level above excludes it: this level cannot allow it (403 otherwise). A
                // model the instance does not offer is never inherited, and reads as not usable.
                const excluded = model.offered && !setting.inherited.includes(model.key);
                const usable = usableAnywhere.has(model.key);
                const on = value.includes(model.key);
                return (
                  <div key={model.key} className="flex flex-wrap items-center gap-x-2.5">
                    <Checkbox
                      id={`${id}-${model.key}`}
                      checked={on}
                      disabled={(excluded || !model.offered) && !on}
                      onCheckedChange={(next) =>
                        setValue(
                          next === true
                            ? props.data.models
                                .map((m) => m.key)
                                .filter((k) => k === model.key || value.includes(k))
                            : value.filter((k) => k !== model.key),
                        )
                      }
                    />
                    <Label htmlFor={`${id}-${model.key}`} className="font-normal">
                      {model.displayName}
                    </Label>
                    {excluded ? (
                      <span className="text-xs text-fg-muted">{t("models.excludedAbove")}</span>
                    ) : usable ? null : (
                      <span className="text-xs text-fg-muted">{t("models.notUsable")}</span>
                    )}
                  </div>
                );
              })}
              {uncovered.length > 0 ? (
                <Notice tone="warning">
                  {t("models.blocked", { roles: roleList(uncovered) })}
                </Notice>
              ) : null}
            </div>
          </PolicyRow>
        </ul>
      ) : null}
    </div>
  );
}
